import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useResource } from "@rest-hooks/core";
import { LoginResource, ProductSelectorType, getCourseStatusResponse } from "@ploy-lib/rest-resources";
import { usePageState } from "@ploy-ui/template-form";
import { DployAutocomplete } from "@ploy-ui/form-fields";
import { makeStyles, getContrastRatio } from "@material-ui/core/styles";
import clsx from "clsx";
import { RouterLink } from "@ploy-ui/core";
import { useNavigate } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
const useStyles = makeStyles(theme => ({
    root: {
        fontWeight: 700,
        [theme.breakpoints.down("xs")]: {
            padding: 4,
            marginTop: 8
        }
    },
    option: {
        padding: 0
    },
    optionLink: {
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 16,
        paddingRight: 16,
        flexGrow: 1,
        color: getContrastRatio(theme.palette.primary.main, theme.palette.common.white) > 2.5
            ? theme.palette.primary.main
            : theme.palette.text.primary
    },
    headingText: {},
    productGroupingText: {
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(3)
    },
    buttonContainer: {},
    buttonMdContainer: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(1)
    },
    buttonPaper: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "8px",
        height: 80,
        marginTop: theme.spacing(2),
        cursor: "pointer",
        width: "300px",
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            boxShadow: theme.shadows[12]
        }
    },
    buttonPaperDisabled: {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "8px",
        height: 80,
        marginTop: theme.spacing(2),
        width: "300px",
        opacity: 0.3,
        cursor: "default"
    },
    buttonItemText: {
        color: theme.palette.primary.contrastText,
        fontWeight: "bold"
    }
}), { name: "DployProductSelector" });
export var ProductSelectorVariant;
(function (ProductSelectorVariant) {
    ProductSelectorVariant["Dropdown"] = "dropdown";
    ProductSelectorVariant["Buttons"] = "buttons";
})(ProductSelectorVariant || (ProductSelectorVariant = {}));
export const ProductSelector = (props) => {
    const { isDirectUser } = useResource(LoginResource.status(), {});
    const { variant = ProductSelectorVariant.Dropdown, ...rest } = props;
    if (isDirectUser)
        return null;
    if (variant === ProductSelectorVariant.Buttons) {
        return _jsx(ProductSelectorButtons, { ...rest });
    }
    return _jsx(ProductSelectorDropdown, { ...rest });
};
const ProductSelectorDropdown = (props) => {
    const { products, selected, disabled, basepath = "" } = props;
    const { step } = usePageState();
    const isHeader = disabled || step > 0 || !products || products.length === 0;
    const items = useMemo(() => (isHeader ? [selected] : products), [isHeader, products, selected]);
    const classes = useStyles(props);
    const navigate = useNavigate();
    return (_jsx(DployAutocomplete, { className: clsx(classes.root), AutocompleteProps: {
            classes: {
                option: classes.option
            },
            getOptionSelected: (option, value) => option?.externalCode === value?.externalCode
        }, "data-test-id": "product-selector", items: items, getItemLabel: p => p?.name ?? "", value: selected, onChange: (e, product) => product && navigate(`${basepath}${product.externalCode.toLowerCase()}`), renderOption: (product, state) => (_jsx(RouterLink, { className: classes.optionLink, underline: "none", to: `${basepath}${product?.externalCode.toLowerCase()}`, "data-product-external-code": product?.externalCode, children: product?.name })), variant: "standard", searchable: false, fullWidth: true, disableClearable: true, readonly: isHeader || items.length === 1 }));
};
const ProductSelectorButtons = (props) => {
    const { products, basepath } = props;
    const classes = useStyles(props);
    const intl = useIntl();
    const renderGroups = () => Object.values(ProductSelectorType).map(productSelectorType => {
        const availableProductsForSelectorType = products.filter(product => product.selectorType === productSelectorType);
        if (availableProductsForSelectorType.length === 0) {
            return null;
        }
        return (_jsxs("div", { children: [_jsx(Typography, { variant: "h5", className: classes.productGroupingText, children: intl.formatMessage(selectorTypeEnumMessages[productSelectorType]) }), _jsx(Grid, { container: true, direction: "row", className: classes.buttonContainer, children: renderFilteredProducts(availableProductsForSelectorType) })] }, productSelectorType));
    });
    const renderFilteredProducts = (availableProducts) => availableProducts.map(product => (_jsx(ProductSelectionItem, { basepath: basepath, product: product }, product.id)));
    return (_jsxs(Grid, { container: true, direction: "column", "data-test-id": "product-selector-buttons", children: [_jsx(Typography, { variant: "h4", color: "secondary", className: classes.headingText, children: _jsx(FormattedMessage, { id: "dealer.productView.productSelector.buttonsVariant.header", description: "header message in productselector", defaultMessage: "Start new process" }) }), renderGroups()] }));
};
const ProductSelectionItem = (props) => {
    const { product, basepath } = props;
    const { name, externalCode } = product;
    const [courses, setCourses] = useState([]);
    const getCourseStatus = useEffect(() => {
        getCourseStatusResponse()
            .then(r => {
            setCourses(r?.courses);
        });
    }, []);
    const blocked = courses !== undefined &&
        courses.length > 0 &&
        courses[0].shouldBlockApplication;
    const classes = useStyles(props);
    const navigate = useNavigate();
    return (_jsx(Grid, { item: true, md: 4, className: classes.buttonMdContainer, children: _jsx(Grid, { item: true, container: true, justifyContent: "center", alignItems: "center", className: blocked ? classes.buttonPaperDisabled : classes.buttonPaper, onClick: () => {
                if (!blocked) {
                    navigate(`${basepath}${externalCode.toLowerCase()}`);
                }
            }, children: _jsx(RouterLink, { underline: "none", to: blocked ? "" : `${basepath}${externalCode.toLowerCase()}`, "data-product-external-code": externalCode, style: { cursor: "inherit" }, children: _jsx(Typography, { className: classes.buttonItemText, children: name }) }) }) }));
};
const selectorTypeEnumMessages = defineMessages({
    [ProductSelectorType.Business]: {
        id: "dealer.productView.productSelector.buttonsVariant.Business",
        description: "ProductSelectorType translation for ProductSelectorType.Businsess",
        defaultMessage: "Business"
    },
    [ProductSelectorType.Calculator]: {
        id: "dealer.productView.productSelector.buttonsVariant.Calculator",
        description: "ProductSelectorType translation for ProductSelectorType.Calculator",
        defaultMessage: "Calculator"
    },
    [ProductSelectorType.NotSet]: {
        id: "dealer.productView.productSelector.buttonsVariant.NotSet",
        description: "ProductSelectorType translation for ProductSelectorType.NotSet",
        defaultMessage: "NotSet"
    },
    [ProductSelectorType.Private]: {
        id: "dealer.productView.productSelector.buttonsVariant.Private",
        description: "ProductSelectorType translation for ProductSelectorType.Private",
        defaultMessage: "Private"
    },
    [ProductSelectorType.Service]: {
        id: "dealer.productView.productSelector.buttonsVariant.Service",
        description: "ProductSelectorType translation for ProductSelectorType.Service",
        defaultMessage: "Service"
    }
});
//# sourceMappingURL=ProductSelector.js.map